import React from 'react';
import { getIsBreakpointGreaterThan } from '@peloton/responsive/reducer';
import type { ClassPreview } from '@engage/library';
import { getActiveCategorySlug } from '@engage/library/selectors';
import { useReduxState } from '@engage/redux';
import { Tiering } from '@members/copy';
import { useIsFreemiumMember } from '@members/tiering/hooks/useIsFreemiumMember';
import classes from './classes.json';

export const useInsertUpsellCard = (classList: ClassPreview[]) => {
  const isFreeUser = useIsFreemiumMember();
  const category = useReduxState(getActiveCategorySlug);
  const index = useReduxState(getIsBreakpointGreaterThan, 'tabletLarge') ? 4 : 2;

  const upsellClass = React.useMemo(() => {
    return getRandomClassByDiscipline(category);
  }, [category]);

  const newClassList = React.useMemo(() => {
    if (!isFreeUser) {
      return classList;
    }
    const first = classList.slice(0, index);
    const rest = classList.slice(index);
    return first.concat(upsellClass as ClassPreview, rest);
  }, [classList, index, isFreeUser, upsellClass]);

  return newClassList;
};

const randomClass = (maxNum: number) => {
  return Math.floor(Math.random() * maxNum);
};

const getRandomClassByDiscipline = (discipline: string) => {
  switch (discipline) {
    case 'all':
      return classes.all[randomClass(classes.all.length)];
    case 'strength':
      return classes.strength[randomClass(classes.strength.length)];
    case 'cardio':
      return classes.cardio[randomClass(classes.cardio.length)];
    case 'meditation':
      return classes.meditation[randomClass(classes.meditation.length)];
    case 'cycling':
      return classes.cycling[randomClass(classes.cycling.length)];
    case 'yoga':
      return classes.yoga[randomClass(classes.yoga.length)];
    case 'stretching':
      return classes.stretching[randomClass(classes.stretching.length)];
    case 'running':
      return classes.running[randomClass(classes.running.length)];
    case 'walking':
      return classes.walking[randomClass(classes.walking.length)];
    case 'bootcamp' || 'tread_bootcamp':
      return classes.tread_bootcamp[randomClass(classes.tread_bootcamp.length)];
    case 'bike_bootcamp':
      return classes.bike_bootcamp[randomClass(classes.bike_bootcamp.length)];
    case 'rowing' || 'caesar':
      return classes.rowing[randomClass(classes.rowing.length)];
    case 'row_bootcamp' || 'caesar_bootcamp':
      return classes.row_bootcamp[randomClass(classes.row_bootcamp.length)];
    default:
      return classes.all[randomClass(classes.all.length)];
  }
};

export const getCopyIdByDiscipline = (discipline: string) => {
  switch (discipline) {
    case 'all':
      return Tiering.ThousandsOfClasses;
    case 'strength':
      return Tiering.MoreStrength;
    case 'cardio':
      return Tiering.MoreCardio;
    case 'meditation':
      return Tiering.MoreMeditation;
    case 'cycling':
      return Tiering.MoreCycling;
    case 'yoga':
      return Tiering.MoreYoga;
    case 'stretching':
      return Tiering.MoreStretching;
    case 'running':
      return Tiering.MoreRunning;
    case 'walking':
      return Tiering.MoreWalking;
    case 'tread_bootcamp' || 'bootcamp':
      return Tiering.MoreTreadBootcamp;
    case 'bike_bootcamp':
      return Tiering.MoreBikeBootcamp;
    case 'rowing' || 'caesar':
      return Tiering.MoreRowing;
    case 'row_bootcamp' || 'caesar_bootcamp':
      return Tiering.MoreRowBootcamp;
    default:
      return Tiering.ThousandsOfClasses;
  }
};
